import { ModalDialog } from '@startuptools/ui/ModalDialog';
import { Modal } from '@startuptools/ui/Modal';
import { ModalClose } from '@startuptools/ui/ModalClose';
import { DialogTitle } from '@startuptools/ui/joy/DialogTitle';
import { DialogContent } from '@startuptools/ui/joy/DialogContent';
import { Trans, useLingui } from '@lingui/react';
import { Suspense } from 'react';
import { CircularProgress } from '@startuptools/ui/CircularProgress';
import { useDeleteDialogSuspenseQuery, useDeleteShareIssueMutation } from './DeleteDialog.graphql';
import { useCompanyId } from '@startuptools/common/react-wrapper';
import { Stack } from '@startuptools/ui/Stack';
import { GqlShareIssue } from '../../graphql/base-types.graphql';
import { AlertConfirmDialogContent } from '../../components/simple-dialogs/AlertConfirmDialog';
import { ShareIssuesDocument } from './ShareIssues.graphql';
import { captureException } from '@sentry/react';
import { genericErrorToast } from '@startuptools/ui/toast/use-toast';
import { AlertDialogContent } from '../../components/simple-dialogs/AlertDialog';
import { Table } from '@startuptools/ui/Table';
import { NumberFormat } from '@startuptools/ui/NumberFormat';
import { DateFormat } from '@startuptools/ui/DateFormat';

const SiDeleteDialogContent = ({
  shareIssue,
  onClose,
}: {
  shareIssue: Pick<GqlShareIssue, 'id' | 'stripeInvoiceId'>;
  onClose: () => unknown;
}) => {
  const companyId = useCompanyId();
  const { i18n } = useLingui();
  const {
    data: { siConnectedWps },
  } = useDeleteDialogSuspenseQuery({ variables: { companyId, id: shareIssue.id }, fetchPolicy: 'cache-and-network' });

  // eslint-disable-next-line sut/loading-mutation
  const [doDelete] = useDeleteShareIssueMutation({ refetchQueries: [ShareIssuesDocument], awaitRefetchQueries: true });

  const handleConfirm = async () => {
    try {
      await doDelete({ variables: { companyId, id: shareIssue.id } });
      onClose();
    } catch (e) {
      captureException(e);
      genericErrorToast();
    }
  };

  if (siConnectedWps?.length) {
    return (
      <>
        <DialogTitle>
          <Trans id="Can't remove the share issue" />
        </DialogTitle>
        <DialogContent>
          <Trans id="You can not remove this share issue because the valuation is being used as in one or more warrant programs" />

          <Table>
            <thead>
              <tr>
                <th>
                  <Trans id="Name" />
                </th>
                <th>
                  <Trans id="Description" />
                </th>
                <th>
                  <Trans id="Number of Options" />
                </th>
                <th>
                  <Trans id="Strike Price" />
                </th>
                <th>
                  <Trans id="Decision Date" />
                </th>
                <th>
                  <Trans id="Start Date" />
                </th>
                <th>
                  <Trans id="First Day to Subscribe" />
                </th>
                <th>
                  <Trans id="Last Day To Subscribe" />
                </th>
              </tr>
            </thead>

            <tbody>
              {siConnectedWps.map(wp => (
                <tr key={wp.id}>
                  <td>{wp.name}</td>
                  <td>{wp.description}</td>
                  <td>
                    <NumberFormat locale={i18n.locale} maximumFractionDigits={0}>
                      {wp.maxNumberOfOptions}
                    </NumberFormat>
                  </td>
                  <td>
                    <NumberFormat locale={i18n.locale} variant="currency" currency={wp.currency}>
                      {wp.strikePrice}
                    </NumberFormat>
                  </td>
                  <td>
                    <DateFormat locale={i18n.locale}>{wp.meetingConcern?.meeting.startTime}</DateFormat>
                  </td>
                  <td>
                    <DateFormat locale={i18n.locale}>{wp.warrantStartDate}</DateFormat>
                  </td>
                  <td>
                    <DateFormat locale={i18n.locale}>{wp.shareSubscribeStartDate}</DateFormat>
                  </td>
                  <td>
                    <DateFormat locale={i18n.locale}>{wp.shareSubscribeEndDate}</DateFormat>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </DialogContent>
      </>
    );
  }

  if (shareIssue.stripeInvoiceId) {
    return (
      <AlertDialogContent title={<Trans id="Can't remove the share issue" />} onAccept={() => null}>
        <Trans id="The share issue you're trying to delete has a paid or pending invoice, contact support if you really want to remove it" />
      </AlertDialogContent>
    );
  }

  return <AlertConfirmDialogContent onClose={onClose} onConfirm={handleConfirm} />;
};

export const SiDeleteDialog = ({
  onClose,
  shareIssue,
}: {
  onClose: () => unknown;
  shareIssue: Pick<GqlShareIssue, 'id' | 'stripeInvoiceId'>;
}) => {
  return (
    <Modal open onClose={onClose}>
      <ModalDialog>
        <ModalClose />
        <Suspense
          fallback={
            <Stack alignItems="center">
              <CircularProgress />
            </Stack>
          }
        >
          <SiDeleteDialogContent shareIssue={shareIssue} onClose={onClose} />
        </Suspense>
      </ModalDialog>
    </Modal>
  );
};
